/**
 * Utility functions for handling date/time in UTC format
 *
 * The backend expects and returns UTC timestamps. All dates are handled in UTC
 * and clearly labeled in the UI to indicate UTC timezone usage.
 */

/**
 * Converts a datetime-local input value to UTC ISO string (Z format)
 * Treats the input as UTC by appending 'Z' to the datetime-local format
 * @param dateTimeLocal - The datetime-local input value (YYYY-MM-DDTHH:mm format)
 * @returns UTC ISO string in Z format, or empty string if input is invalid
 */
export function toISOString(dateTimeLocal: string): string {
  if (!dateTimeLocal) return '';

  try {
    // Append 'Z' to treat the datetime-local input as UTC
    const utcDate = new Date(`${dateTimeLocal}Z`);

    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      return '';
    }

    return utcDate.toISOString();
  } catch (error) {
    console.warn('Failed to convert datetime-local to ISO string:', error);

    return '';
  }
}

/**
 * Extracts datetime-local format from UTC ISO string
 * @param isoString - The UTC datetime string in ISO format (with Z)
 * @returns Datetime string in YYYY-MM-DDTHH:mm format for datetime-local input
 */
export function fromISOString(isoString: string): string {
  if (!isoString) return '';

  try {
    // Validate that we have a proper date first
    const date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Extract the first 16 characters (YYYY-MM-DDTHH:mm) from ISO string
    // This works because ISO format is YYYY-MM-DDTHH:mm:ss.sssZ
    return isoString.slice(0, 16);
  } catch (error) {
    console.warn('Failed to extract datetime-local from ISO string:', error);

    return '';
  }
}

/**
 * Formats a UTC datetime string for display in UTC timezone
 * @param isoString - The UTC datetime string in ISO format (with Z)
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted UTC datetime string for display
 */
export function formatForDisplay(
  isoString: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  }
): string {
  if (!isoString) return '';

  try {
    const date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString(undefined, options);
  } catch (error) {
    console.warn('Failed to format datetime for display:', error);

    return '';
  }
}

/**
 * Validates that a datetime string is in valid ISO format
 * @param dateTime - The datetime string to validate
 * @returns true if valid, false otherwise
 */
export function isValidDateTime(dateTime: string): boolean {
  if (!dateTime) return false;

  try {
    const date = new Date(dateTime);

    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}
